// SupportBell — chuông thông báo YÊU CẦU HỖ TRỢ trên thanh trên cùng (giao diện v2), cho hub KHÔNG có chuông
// «Tin mới» (Site Connect). Sếp Phú 13/10/2026: «siteconnect … sao ko thấy chuông thông báo» — thông báo giống
// worksaas: đội hỗ trợ đổi trạng thái / trả lời một yêu cầu của workspace ⇒ số đỏ trên chuông, mở ra thấy
// dòng cập nhật, bấm là mở khung hỗ trợ ngay tại yêu cầu đó.
//
// Work KHÔNG gắn chuông này: chuông «Tin mới» của Work (product-news/ProductNewsBell) đã trộn sẵn dòng yêu
// cầu — gắn thêm là hai chuông cùng một thông báo.
//
// Dữ liệu = kho supportTicketFeed (đọc từ CHÍNH widget hỗ trợ, phạm vi cả workspace khi máy chủ đã ký claim).
// Chữ trung tính — hub Site Connect không được lộ thương hiệu CNV (tools/check-brand-leak.js của Connect).
// Chuông CHỈ hiện khi widget hỗ trợ đã dựng xong (cùng điều kiện nút Trợ giúp).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bell, CheckCircle2, ChevronRight, LifeBuoy } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { getHelpState, subscribeHelp, openHelpList, OVER_WIDGET_Z } from './helpWidget';
import {
  useSupportTickets, refreshSupportTickets, openSupportTicket, markAllSupportTicketsSeen,
} from '../product-news/supportTicketFeed';
import { fmtDate, fmtDateTime } from '../../i18n/format.js';

const PANEL_W = 400;

/** Màu nhãn trạng thái — cùng ba tông widget dùng (open | wait | done). */
const TONE = {
  open: 'bg-sky-50 text-sky-700 border-sky-200',
  wait: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/** Khoá ngày để nhóm dòng — cùng múi với fmtDate (i18n/format.js giữ múi hiển thị của host). */
function dayKey(iso) {
  try { return fmtDate(iso); } catch { return ''; }
}

/** Thả bảng xuống dưới nút, canh mép phải theo nút; đo rect TRƯỚC khi đổi state. */
function placePanel(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(PANEL_W, vw - 16);
  if (!rect) return { top: 56, left: Math.max(8, vw - width - 8), width, maxHeight: vh - 72 };
  const top = rect.bottom + 8;
  const left = Math.max(8, Math.min(rect.right - width, vw - width - 8));
  return { top, left, width, maxHeight: Math.max(240, vh - top - 16) };
}

export default function SupportBell() {
  const { t } = useLingui();
  const [help, setHelp] = useState(getHelpState);
  const tk = useSupportTickets();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => subscribeHelp(setHelp), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useLayoutEffect(() => { if (!open) setTab('all'); }, [open]);

  if (!help.ready) return null;

  const unread = tk.unread;
  const items = tab === 'unread' ? tk.items.filter((i) => i.has_news) : tk.items;
  const groups = [];
  for (const it of items) {
    const k = dayKey(it.at);
    const last = groups[groups.length - 1];
    if (!last || last.key !== k) groups.push({ key: k, date: it.at, rows: [it] });
    else last.rows.push(it);
  }
  const todayKey = dayKey(new Date().toISOString());
  const badge = unread > 9 ? '9+' : String(unread);
  const label = unread > 0 ? t`Thông báo hỗ trợ — ${unread} chưa đọc` : t`Thông báo hỗ trợ`;

  const toggle = () => {
    if (open) { setOpen(false); return; }
    setPos(placePanel(btnRef.current?.getBoundingClientRect() || null));
    setOpen(true);
    refreshSupportTickets({ force: true });
  };
  const openRow = (it) => { setOpen(false); openSupportTicket(it.id); };

  return (
    <>
      <button ref={btnRef} type="button" className="v2-topbar__icon" onClick={toggle} title={label} aria-label={label}>
        <Bell size={16} />
        {unread > 0 && <span className="v2-topbar__dot" aria-hidden="true">{badge}</span>}
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} role="dialog" aria-label={t`Thông báo hỗ trợ`}
             className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden text-gray-800"
             style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight, zIndex: OVER_WIDGET_Z }}>
          <div className="px-5 pt-4 pb-3 border-b border-gray-100">
            <div className="text-base font-bold text-gray-900">{t`Thông báo hỗ trợ`}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {tk.scope === 'workspace' ? t`Cập nhật yêu cầu hỗ trợ của workspace` : t`Cập nhật yêu cầu hỗ trợ của bạn`}
            </div>
          </div>

          <div className="px-4 py-2.5 flex items-center justify-between gap-2 border-b border-gray-100">
            <div className="inline-flex p-0.5 rounded-xl bg-gray-100 text-sm">
              <button type="button" onClick={() => setTab('all')}
                      className={`px-3 py-1 rounded-lg ${tab === 'all' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {t`Tất cả`}
              </button>
              <button type="button" onClick={() => setTab('unread')}
                      className={`px-3 py-1 rounded-lg ${tab === 'unread' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {t`Chưa đọc`}{unread > 0 && <span className="ml-1 text-rose-600 font-semibold">{unread}</span>}
              </button>
            </div>
            <button type="button" onClick={() => markAllSupportTicketsSeen()} disabled={unread === 0}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default">
              <CheckCircle2 size={14} /> {t`Đánh dấu đã đọc`}
            </button>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0">
            {items.length === 0 ? (
              <div className="py-12 px-6 text-center text-sm text-gray-400">
                {tab === 'unread' ? t`Bạn đã đọc hết thông báo.` : t`Chưa có cập nhật nào.`}
              </div>
            ) : groups.map((g) => (
              <div key={g.key}>
                <div className="flex items-center gap-3 px-5 pt-3 pb-1">
                  <span className="h-px flex-1 bg-gray-100" />
                  <span className="text-[11px] font-medium text-gray-400">{g.key === todayKey ? t`Hôm nay` : fmtDate(g.date)}</span>
                  <span className="h-px flex-1 bg-gray-100" />
                </div>
                {g.rows.map((it) => {
                  const who = it.by || t`Đội hỗ trợ`;
                  return (
                    <button key={it.id} type="button" onClick={() => openRow(it)}
                            className="w-full text-left px-5 py-3 flex gap-3 hover:bg-gray-50 transition-colors">
                      <span className="w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                        <LifeBuoy size={18} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[11px] text-gray-400">{fmtDateTime(it.at)}</span>
                        <span className={`mt-0.5 block text-sm leading-snug line-clamp-2 ${it.has_news ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {it.code ? `${it.code} · ` : ''}{it.title}
                        </span>
                        {it.kind === 'reply' && it.excerpt ? (
                          <span className="mt-0.5 block text-[13px] leading-snug text-gray-500 line-clamp-2">{t`${who} trả lời: ${it.excerpt}`}</span>
                        ) : it.status_label ? (
                          <span className="mt-1 flex items-center gap-1.5 text-[12px] text-gray-500">
                            {t`Trạng thái:`}
                            <span className={`inline-flex px-1.5 py-px rounded-md border text-[11px] font-medium ${TONE[it.status_tone] || TONE.open}`}>{it.status_label}</span>
                          </span>
                        ) : null}
                        {!it.mine && it.requester_name ? (
                          <span className="mt-0.5 block text-[11px] text-gray-400 truncate">{t`Gửi bởi ${it.requester_name}`}</span>
                        ) : null}
                      </span>
                      {it.has_news && <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 shrink-0" aria-label={t`Chưa đọc`} />}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <button type="button" onClick={() => { setOpen(false); openHelpList(); }}
                  className="px-5 py-3 border-t border-gray-100 text-sm font-medium text-blue-600 hover:bg-blue-50/60 inline-flex items-center justify-center gap-1">
            {t`Xem tất cả yêu cầu`} <ChevronRight size={16} />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}
